import { Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { useAuth } from './context/AuthContext';

// Layout
import Navbar from './components/layout/Navbar';
import Footer from './components/layout/Footer';
import Sidebar from './components/layout/Sidebar';

// Public pages
import Home from './pages/public/Home';
import AIBox from './pages/public/AIBox';
import About from './pages/public/About';
import Products from './pages/public/Products';
import Contact from './pages/public/Contact';

// Auth
import Login from './pages/auth/Login';

// Central (client portal)
import CentralDashboard from './pages/central/Dashboard';
import Activate from './pages/central/Activate';
import Licenses from './pages/central/Licenses';
import Warranty from './pages/central/Warranty';
import Support from './pages/central/Support';
import TicketDetail from './pages/central/TicketDetail';
import Profile from './pages/central/Profile';

// Admin
import AdminDashboard from './pages/admin/AdminDashboard';
import IssueCertificate from './pages/admin/IssueCertificate';
import ManageCertificates from './pages/admin/ManageCertificates';
import ManageOrganizations from './pages/admin/ManageOrganizations';
import ManageUsers from './pages/admin/ManageUsers';
import Payments from './pages/admin/Payments';
import AuditLog from './pages/admin/AuditLog';
import Messages from './pages/admin/Messages';
import AdminProducts from './pages/admin/AdminProducts';
import ProductDetail from './pages/admin/ProductDetail';
import ImportExcel from './pages/admin/ImportExcel';
import Help from './pages/admin/Help';

// Public layout wrapper
function PublicLayout() {
  return (
    <div className="min-h-screen bg-navy-900">
      <Navbar />
      <main className="pt-16">
        <Outlet />
      </main>
      <Footer />
    </div>
  );
}

// Portal layout wrapper (Sidebar + content area)
function PortalLayout() {
  const { loading, user } = useAuth();
  if (loading) return <LoadingScreen />;
  if (!user) return <Navigate to="/login" replace />;

  return (
    <div className="min-h-screen bg-[#060a10]">
      <Sidebar />
      <main className="ml-60 min-h-screen" style={{ padding: '24px 28px 60px' }}>
        <Outlet />
      </main>
    </div>
  );
}

// Admin layout — requires admin role
function AdminLayout() {
  const { loading, user, isAdmin } = useAuth();
  if (loading) return <LoadingScreen />;
  if (!user) return <Navigate to="/login" replace />;
  if (!isAdmin) return <Navigate to="/central" replace />;

  return (
    <div className="min-h-screen bg-[#060a10]">
      <Sidebar />
      <main className="ml-60 min-h-screen" style={{ padding: '24px 28px 60px' }}>
        <Outlet />
      </main>
    </div>
  );
}

function LoadingScreen() {
  return (
    <div className="min-h-screen bg-[#060a10] flex items-center justify-center">
      <div className="text-center">
        <div className="w-12 h-12 border-4 border-[#06b6d4] border-t-transparent rounded-full animate-spin mx-auto" />
        <p className="mt-4 text-[#64748b]">Loading...</p>
      </div>
    </div>
  );
}

export default function App() {
  return (
    <>
      <Toaster
        position="top-right"
        toastOptions={{
          style: { background: '#0f1623', color: '#e2e8f0', border: '1px solid rgba(148,163,184,0.06)', fontFamily: 'Outfit, system-ui, sans-serif' },
        }}
      />
      <Routes>
        {/* Public pages */}
        <Route element={<PublicLayout />}>
          <Route index element={<Home />} />
          <Route path="/aibox" element={<AIBox />} />
          <Route path="/about" element={<About />} />
          <Route path="/products" element={<Products />} />
          <Route path="/contact" element={<Contact />} />
        </Route>

        {/* Auth */}
        <Route path="/login" element={<Login />} />

        {/* Client portal */}
        <Route path="/central" element={<PortalLayout />}>
          <Route index element={<CentralDashboard />} />
          <Route path="activate" element={<Activate />} />
          <Route path="licenses" element={<Licenses />} />
          <Route path="warranty" element={<Warranty />} />
          <Route path="support" element={<Support />} />
          <Route path="support/:id" element={<TicketDetail />} />
          <Route path="profile" element={<Profile />} />
        </Route>

        {/* Admin */}
        <Route path="/admin" element={<AdminLayout />}>
          <Route index element={<AdminDashboard />} />
          <Route path="products" element={<AdminProducts />} />
          <Route path="products/:id" element={<ProductDetail />} />
          <Route path="import" element={<ImportExcel />} />
          <Route path="certificates/new" element={<IssueCertificate />} />
          <Route path="certificates" element={<ManageCertificates />} />
          <Route path="organizations" element={<ManageOrganizations />} />
          <Route path="users" element={<ManageUsers />} />
          <Route path="warranty" element={<Payments />} />
          <Route path="messages" element={<Messages />} />
          <Route path="audit" element={<AuditLog />} />
          <Route path="help" element={<Help />} />
        </Route>

        {/* Catch-all */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  );
}
